import * as React from 'react'
import ErrorBoxComponent from '../../../shared/error-box.component'
import CesuPriceableFormComponent from '../../cesu-priceable/cesu-priceable-form.component'
import { CesuQuotationBase } from '../../../../services/json-schemas/base/cesu_quotation'
import { CesuMileageReimbursementBase } from '../../../../services/json-schemas/base/cesu_mileage_reimbursement'
import { CesuTransportFeeBase } from '../../../../services/json-schemas/base/cesu_transport_fee'
import { MarketplaceBase } from '../../../../services/json-schemas/base/marketplace'
import { QuotationBase } from '../../../../services/json-schemas/base/quotation'
import QuotationDescriptionInputComponent from './quotation-description-input.component'
import { OfferedServiceBase } from '../../../../services/json-schemas/base/offered_service'
import { I18nService } from '../../../../services/utils/i18n.service'

interface Props {
  castingMarket: Pick<MarketplaceBase, 'currency'>
  offeredService: Pick<OfferedServiceBase, 'description_plain_text' | 'employee_of_customer_on_duty_presence'>
  quotation: Pick<QuotationBase, 'description'> & {
    cesu_quotation_attributes: Pick<
      CesuQuotationBase, 'id' | 'management_fee_with_taxes_cents' | 'management_fee_unit_of_work' | 'gross_employee_hourly_salary_cents' | 'employer_charge_percentage' | 'charge_exemption' |
      'estimated_monthly_hours' | 'rest_week_day' | 'rest_week_day_worked_paid' | 'high_value_day_offs' | 'day_off_increase_percentage' | 'weekend_increase_percentage' | 'weekend_incluses_saturday' |
      'apply_increases_to_management_fees' | 'on_duty_presence_night_start' | 'on_duty_presence_night_end' | 'default_interrupt_count'
    > & {
      cesu_mileage_reimbursement_attributes: Pick<CesuMileageReimbursementBase, 'id' | 'amount_cents' | 'applyness' | 'show_on_document'> & { _destroy?: boolean } | null
      cesu_transport_fee_attributes: Pick<CesuTransportFeeBase, 'id' | 'amount_cents' | 'applyness' | 'show_on_document'> & { _destroy?: boolean } | null
    }
  }
  onChange: React.Dispatch<React.SetStateAction<Partial<Props['quotation']>>>
}

const i18nScoped = new I18nService(require('./quotation-form-enable-employee-of-customer.component.yml'))

export default function QuotationFormEnableEmployeeOfCustomerComponent ({ castingMarket, offeredService, quotation, onChange }: Props) {
  const [estimationErrors, setEstimationErrors] = React.useState<string[]>([])

  const setCesuQuotationState = React.useCallback((partial: Partial<Props['quotation']['cesu_quotation_attributes']>) => {
    onChange(quotation => ({ ...quotation, cesu_quotation_attributes: { ...quotation.cesu_quotation_attributes, ...partial } }))
  }, [onChange]) // should only rely on store for immutable reference, required by CesuPricingFormComponent
  
  return <>
    <ErrorBoxComponent fullMessages={ estimationErrors } />

    <CesuPriceableFormComponent object={ quotation.cesu_quotation_attributes } setObjectState={ setCesuQuotationState } market={ castingMarket } setEstimationErrors={ setEstimationErrors }>
      { ({ managementFeeField, salaryField, estimationBlock, additionalCompensationsField, additionalCompensationBlock, onDutyPresenceNightFields, applyIncreasesToManagementFeesField, restWeekDayField, restWeekDayWorkedPaidField, highValueDayOffsField, dayOffIncreasePercentageField, weekendIncreaseField }) => <>
        { managementFeeField }
        { salaryField }
        { offeredService.employee_of_customer_on_duty_presence && <div className="u-mts">{ onDutyPresenceNightFields }</div> }
        { estimationBlock }

        <div className="u-borderT u-mts u-mbm" />
        <label className="form-label">{ i18nScoped.t('increase_rules') }</label>
        <div className="u-mbs">{ applyIncreasesToManagementFeesField }</div>
        <div className="grid">
          <div className="grid-1-2">{ weekendIncreaseField }</div>
          <div className="grid-1-2 u-mbs">{ highValueDayOffsField }</div>
          <div className="grid-1-2 u-mbs">{ dayOffIncreasePercentageField }</div>
          <div className="grid-1-2 u-mbs">{ restWeekDayField }</div>
          <div className="grid-4-4 u-mbs">{ restWeekDayWorkedPaidField }</div>
        </div>

        <div className="u-borderT u-mts u-mbm" />
        <div className='u-mbs'>{ additionalCompensationsField }</div>
        { additionalCompensationBlock }
      </> }
    </CesuPriceableFormComponent>

    <QuotationDescriptionInputComponent
      className="u-mtm"
      description={ quotation.description }
      offeredServiceDescriptionPlainText={ offeredService.description_plain_text }
      placeholder={ i18nScoped.t('description_placeholder') }
      rows={ 3 }
      onChange={ description => onChange({ description }) } />
  </>
}